import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PLANBASE_CONFIG } from '../config';
import { decodeJWT } from '../utils/auth';
import { Spinner } from '../components/ui/spinner';

/**
 * Fire-and-forget telemetry — logs the raw OAuth payload server-side
 * so every failure is captured without asking the user for anything.
 * Visible via `npx wrangler tail --config worker/wrangler.toml`.
 */
function reportAuthFailure(payload: {
  stage: string;
  message: string;
  rawSession?: string | null;
  rawUser?: string | null;
}) {
  try {
    fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/auth/callback-error`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...payload,
        url: window.location.href,
        userAgent: navigator.userAgent,
      }),
    }).catch(() => { /* best-effort */ });
  } catch { /* ignore */ }
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    // Handle OAuth callback
    const handleCallback = async () => {
      try {
        const queryParams = new URLSearchParams(window.location.search);
        
        // Debug: Log all query parameters
        console.log('AuthCallback - Current URL:', window.location.href);
        console.log('AuthCallback - Query params:', Object.fromEntries(queryParams.entries()));
        
        // Check for Gately token (from SSO redirect)
        const gatelyToken = queryParams.get('gately_token');
        const gatelySession = queryParams.get('gately_session');
        const gatelyUser = queryParams.get('gately_user');
        const authSuccess = queryParams.get('gately_auth_success');
        const errorParam = queryParams.get('error');
        
        console.log('AuthCallback - gatelyToken:', gatelyToken ? 'present' : 'missing');
        console.log('AuthCallback - gatelySession:', gatelySession ? 'present' : 'missing');
        console.log('AuthCallback - gatelyUser:', gatelyUser ? 'present' : 'missing');
        console.log('AuthCallback - authSuccess:', authSuccess);
        console.log('AuthCallback - error:', errorParam);
        
        if (errorParam) {
          setError(errorParam);
          setTimeout(() => navigate('/login'), 2000);
          return;
        }
        
        // Handle new format with gately_session and gately_user
        if (gatelySession && gatelyUser && authSuccess === 'true') {
          try {
            // Robust parser — some auth providers URL-encode twice,
            // some base64-encode the payload, mobile Safari sometimes
            // mangles special characters. Try each decoder in order
            // and keep the first that yields valid JSON. Logs the raw
            // input so a repeat failure can be diagnosed from the
            // console.
            const robustParse = (raw: string, label: string): any => {
              const attempts = [
                () => JSON.parse(raw),
                () => JSON.parse(decodeURIComponent(raw)),
                () => JSON.parse(atob(raw)),
                () => JSON.parse(atob(raw.replace(/-/g, '+').replace(/_/g, '/'))),
              ];
              for (const fn of attempts) {
                try { return fn(); } catch { /* try next */ }
              }
              console.error(`AuthCallback - all parse attempts failed for ${label}. Raw value (first 200 chars):`, raw.slice(0, 200));
              throw new Error(`Could not parse ${label}`);
            };
            const sessionData = robustParse(gatelySession, 'session');
            const userData = robustParse(gatelyUser, 'user');

            console.log('AuthCallback - Parsed session data:', sessionData);
            console.log('AuthCallback - Parsed user data:', userData);
            
            // Extract token from session
            const token = sessionData.access_token;
            if (!token) {
              throw new Error('No access token in session data');
            }
            
            // Store the token
            localStorage.setItem('planbase_token', token);
            
            // Construct user object from the provided data
            const user = {
              id: userData.id,
              email: userData.email,
              name: userData.name || userData.full_name || userData.email?.split('@')[0] || 'User',
              avatar_url: userData.avatar_url,
              title: userData.metadata?.title || 'Architect',
              metadata: {
                ...userData.metadata,
                full_name: userData.name || userData.full_name
              }
            };
            
            localStorage.setItem('planbase_user', JSON.stringify(user));
            
            // Always try to notify opener and close — works when window.opener is available.
            // When opener is null (cross-origin redirect stripped it), the localStorage
            // polling in AuthContext will detect the token and resolve instead.
            try {
              if (window.opener && !window.opener.closed) {
                window.opener.postMessage({ type: 'GATELY_OAUTH_SUCCESS' }, window.location.origin);
              }
            } catch (_) {
              // opener may be cross-origin restricted — polling fallback handles it
            }
            // Always attempt to close the popup
            window.close();
            
            // If window.close() didn't work (e.g. not opened by script), fall through
            // to direct navigation after a short delay
            setTimeout(() => {
              if (!window.closed) {
                window.dispatchEvent(new Event('auth-changed'));
                navigate('/welcome');
              }
            }, 300);
            return;
          } catch (parseError: any) {
            console.error('AuthCallback - Failed to parse session/user data:', parseError);
            // Server-side telemetry — sends the raw payload to the
            // worker so we can see what the provider actually returned
            // without having to ask the user to copy anything.
            reportAuthFailure({
              stage: 'parse-session-user',
              message: parseError?.message || 'parse failed',
              rawSession: gatelySession,
              rawUser: gatelyUser,
            });
            // Last-ditch fallback: if we have a usable gately_token in
            // the URL at all, fall through to the token-only branch
            // below rather than failing out. Covers the case where
            // one of the two payloads got mangled but the token is
            // still intact.
            if (!gatelyToken) {
              setError(parseError?.message || "We couldn't finish signing you in. Try again.");
              return;
            }
          }
        }
        
        // Handle legacy format with gately_token (fallback)
        if (gatelyToken && authSuccess === 'true') {
          // Store the token temporarily
          localStorage.setItem('planbase_token', gatelyToken);
          
          // Decode JWT to get user ID
          try {
            const payload = decodeJWT(gatelyToken);
            
            const userId = payload?.userId || payload?.sub;
            const projectId = payload.project_id || PLANBASE_CONFIG.projectId;
            
            // Fetch full user data from API
            const userResponse = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/members/${userId}`, {
              headers: {
                'Authorization': `Bearer ${gatelyToken}`,
                'X-Project-ID': projectId,
                'Content-Type': 'application/json'
              }
            });
            
            if (!userResponse.ok) {
              throw new Error('Failed to fetch user data');
            }
            
            const userData = await userResponse.json();
            
            // Extract member data
            const member = userData.member || userData;
            
            // Parse metadata if it's a string
            let metadata = member.metadata;
            if (typeof metadata === 'string') {
              try {
                metadata = JSON.parse(metadata);
              } catch (e) {
                metadata = {};
              }
            }
            
            // Construct full name from available fields
            const fullName = metadata.full_name || 
                           (metadata.first_name && metadata.last_name 
                             ? `${metadata.first_name} ${metadata.last_name}` 
                             : null) ||
                           metadata.first_name ||
                           metadata.name ||
                           member.email?.split('@')[0] || 
                           'User';
            
            const user = {
              id: member.id,
              email: member.email,
              name: fullName,
              avatar_url: metadata.avatar_url,
              title: metadata.title || 'Architect',
              metadata: {
                ...metadata,
                full_name: fullName
              }
            };
            
            localStorage.setItem('planbase_user', JSON.stringify(user));
            
            // Always try to notify opener and close
            try {
              if (window.opener && !window.opener.closed) {
                window.opener.postMessage({ type: 'GATELY_OAUTH_SUCCESS' }, window.location.origin);
              }
            } catch (_) {
              // opener may be cross-origin restricted — polling fallback handles it
            }
            window.close();

            setTimeout(() => {
              if (!window.closed) {
                window.dispatchEvent(new Event('auth-changed'));
                navigate('/welcome');
              }
            }, 300);
          } catch (decodeError: any) {
            reportAuthFailure({ stage: 'decode-jwt', message: decodeError?.message || 'decode failed' });
            setError(decodeError.message || "We couldn't finish signing you in. Try again.");
          }
        } else {
          reportAuthFailure({ stage: 'no-token', message: 'callback hit without gately_token / session' });
          setError("We couldn't finish signing you in. Try again.");
        }
      } catch (err: any) {
        reportAuthFailure({ stage: 'unhandled', message: err?.message || 'unknown' });
        setError(err.message || "We couldn't finish signing you in. Try again.");
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background px-6">
      <div className="w-full max-w-sm text-center">
        {error ? (
          <>
            <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.75-2.98l-7.07-12a2 2 0 00-3.5 0l-7.07 12A2 2 0 004.93 19z" />
              </svg>
            </div>
            <p className="text-lg font-medium text-foreground mb-2">Sign-in didn't finish</p>
            <p className="text-sm text-muted-foreground mb-5">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Try again
            </button>
            <p className="text-[11px] text-muted-foreground/70 mt-4">
              This has been reported automatically. You don't need to do anything else.
            </p>
          </>
        ) : (
          <>
            <Spinner size="lg" className="mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground">Signing you in…</p>
            <p className="text-sm text-muted-foreground mt-2">One moment</p>
          </>
        )}
      </div>
    </div>
  );
}
